import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getLogEntry } from '@/features/elva-work/store';
import type { Posting } from '../types';
import { amountAria, cardChargedLine, money, signedMoney, wholeMoney } from '../utils';
import { POSTED_GROUP_TOTALS, POSTED_SUMMARY } from '../fixtures';
import { Check, Spinner } from './common';
import { undoPosting } from '../store';
import { cardFeeLine, dentistShort, isOon, lineForNetwork, noWriteOffLabel, referenceLine } from '../reference';
import { PMS } from '@/features/shared/pms';
import { hasMoneyPermission } from '@/features/shared/permissions';

/** Postings that have their own entry in ELVA's log (real ids only; no entry, no link). */
const LOG_ENTRY_FOR_POSTING: Record<string, string> = { 'PD-JORGE': 'log-post-jorge' };

/** A balance in words: a negative one is a patient credit. */
function balanceWords(n: number): string {
  return n < 0 ? `${money(n)} in credit` : money(n);
}

/** The line under the name: how the money arrived (D199 reference sits under "What was posted"). */
function paymentMeta(p: Posting): string {
  if (p.payment.method === 'check') return `Paper check ${p.payment.checkNo ?? ''}`.trim();
  if (p.payment.method === 'eob') return `EOB of ${p.payment.eobDate ?? p.eraDisplay} · no deposit`;
  // D195/D283: a card payment is charged, never "Matched" to a bank deposit.
  if (p.payment.method === 'card') return cardChargedLine(p.payment.approval ?? '');
  return `ERA of ${p.eraDisplay} · trace ${p.trace}`;
}

function byLine(p: Posting): string {
  if (p.confirmedBy === 'autopilot') return 'Autopilot';
  if (p.confirmedBy === 'self') return `posted by Ava Thompson in ${PMS}`;
  const a = p.confirmedBy;
  return `confirmed by ${a.name}`;
}

function Row({ p, selected, onSelect }: { p: Posting; selected: boolean; onSelect: (id: string) => void }) {
  return (
    <Link to={`/posting/posted/${p.id}`} className={`po-qrow${selected ? ' po-qsel' : ''}`} aria-current={selected ? 'page' : undefined} onClick={() => { onSelect(p.id); }}>
      <div className="po-qtop">
        <span className="po-qname">{p.isUndo ? 'Undo of ' : ''}{p.patient}</span>
        <span className={`po-qamt num${p.undone ? ' po-struck' : ''}`} aria-label={amountAria(p.amount)}>{signedMoney(p.amount)}</span>
      </div>
      <div className="po-qsub">{p.payer.name}</div>
      <div className="po-qtagline">
        {p.undone ? <span className="po-badge b-mute">Undone</span> : null}
        {p.isUndo ? <span className="po-badge b-mute">Reversal</span> : null}
        <span className="po-meta">{p.postedDay === 'earlier' ? `${p.postedDateLabel} · ${p.postedTime}` : p.postedTime} · {byLine(p)}</span>
      </div>
    </Link>
  );
}

const byNewest = (a: Posting, b: Posting): number => (a.postedIso < b.postedIso ? 1 : a.postedIso > b.postedIso ? -1 : 0);

export function PostedList({ postings, selectedId, onSelect }: { postings: Posting[]; selectedId?: string; onSelect: (id: string) => void }) {
  const [q, setQ] = useState('');
  const [yOpen, setYOpen] = useState(false);
  const [eOpen, setEOpen] = useState(false);
  const match = (p: Posting): boolean => !q.trim() || p.patient.toLowerCase().includes(q.trim().toLowerCase()) || p.payer.name.toLowerCase().includes(q.trim().toLowerCase());
  const today = postings.filter((p) => p.postedDay === 'today' && match(p)).sort(byNewest);
  const yest = postings.filter((p) => p.postedDay === 'yesterday' && match(p)).sort(byNewest);
  const earlier = postings.filter((p) => p.postedDay === 'earlier' && match(p)).sort(byNewest);
  return (
    <div className="po-queue">
      <div className="po-search-wrap">
        <input className="po-search" type="search" placeholder="Search postings" aria-label="Search postings" value={q} onChange={(e) => { setQ(e.target.value); }} />
      </div>
      <div className="po-monthsel">
        <label className="po-meta" htmlFor="po-month">Month</label>
        <select id="po-month" className="po-select" style={{ width: 'auto' }} defaultValue="October"><option>October</option><option>September</option></select>
      </div>
      <div className="po-posted-summary">
        <strong>{POSTED_SUMMARY.month} so far: {POSTED_SUMMARY.count} payments, {money(POSTED_SUMMARY.total)}</strong>. {POSTED_SUMMARY.trainingLine}
      </div>
      <p className="po-group-head">Today · <span className="po-count">{today.length}</span></p>
      {today.map((p) => <Row key={p.id} p={p} selected={p.id === selectedId} onSelect={onSelect} />)}
      <p className="po-group-head">Yesterday · <span className="po-count">{POSTED_GROUP_TOTALS.yesterday.count}</span></p>
      {yOpen ? yest.map((p) => <Row key={p.id} p={p} selected={p.id === selectedId} onSelect={onSelect} />) : (
        <button type="button" className="po-btn po-btn-ghost po-btn-sm" style={{ margin: '8px 16px' }} onClick={() => { setYOpen(true); }}>Show {POSTED_GROUP_TOTALS.yesterday.count} postings · {wholeMoney(POSTED_GROUP_TOTALS.yesterday.total)}</button>
      )}
      <p className="po-group-head">Earlier in October · <span className="po-count">{POSTED_GROUP_TOTALS.earlier.count}</span></p>
      {eOpen ? earlier.map((p) => <Row key={p.id} p={p} selected={p.id === selectedId} onSelect={onSelect} />) : (
        <button type="button" className="po-btn po-btn-ghost po-btn-sm" style={{ margin: '8px 16px' }} onClick={() => { setEOpen(true); }}>Show {POSTED_GROUP_TOTALS.earlier.count} postings · {wholeMoney(POSTED_GROUP_TOTALS.earlier.total)}</button>
      )}
    </div>
  );
}

export function PostedDetail({ p, onBack }: { p: Posting; onBack?: () => void }) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [confirm, setConfirm] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [undoStep, setUndoStep] = useState('');
  useEffect(() => { headingRef.current?.focus(); }, [p.id]);

  const runUndo = async (): Promise<void> => {
    setConfirm(false);
    setUndoing(true);
    for await (const ev of undoPosting(p.id)) {
      if ('step' in ev) setUndoStep(ev.step);
    }
    setUndoing(false);
  };

  const oon = isOon(p);
  const lines = p.lines.map((l) => lineForNetwork(l, oon));
  const wroteOff = oon ? p.lines.reduce((t, l) => t + l.writeOff, 0) : 0;
  const balanceAfter = Math.round((p.balanceAfter + wroteOff) * 100) / 100;
  const fee = cardFeeLine(p.payment, p.amount);
  const codes =
    p.kind === 'paid_to_patient'
      ? 'Codes on the EOB: CARC 100 (payment made to the patient).'
      : oon
        ? `Codes on the ERA: CO-45 (not posted as a write-off: ${dentistShort(p.dentist)} is out of network with ${p.payer.short}), PR-2 (coinsurance).`
        : 'Codes on the ERA: CO-45 (write-off), PR-2 (coinsurance).';

  return (
    <main className="po-detail">
      {onBack ? <button type="button" className="po-back" onClick={onBack}>← All postings</button> : null}
      <div className="po-detail-inner">
        <section className="po-claimhead">
          <div>
            <h2 className="po-patient" tabIndex={-1} ref={headingRef}>{p.patient}</h2>
            <p className="po-chsub">{p.payer.name} · member {p.memberId} · {p.claimOf}{oon ? ` · ${dentistShort(p.dentist)} · out of network with ${p.payer.short}` : ''}</p>
            <p className="po-chmeta">Posted in {PMS} {p.postedDay === 'today' ? `today at ${p.postedTime}` : `${p.postedDateLabel} at ${p.postedTime}`} · {paymentMeta(p)}</p>
          </div>
          <div className="po-right"><div className="po-meta">{p.amountLabel}</div><div className="po-bigfig num" aria-label={amountAria(p.amount)}>{signedMoney(p.amount)}</div></div>
        </section>

        <div className="po-srcrow" style={{ gap: 8 }}>
          {p.undone ? (
            <span className="po-badge b-mute">Undone</span>
          ) : p.isUndo ? (
            <span className="po-badge b-mute">Reversal</span>
          ) : p.alreadyPosted ? (
            <span className="po-badge b-mute">Already posted in {PMS}</span>
          ) : (
            <span className="po-badge b-ok"><Check /> Posted and checked</span>
          )}
          {p.alreadyPosted ? (
            <span className="po-badge b-mute">Posted by your team</span>
          ) : (
            <>
              <span className="po-badge b-mute">{p.confirmedBy === 'autopilot' ? 'Autopilot' : p.confirmedBy === 'self' ? `Posted by Ava Thompson in ${PMS}` : `Confirmed by ${(p.confirmedBy).name} · ${(p.confirmedBy).org}`}</span>
              <span className="po-badge b-info">{p.how === 'api' ? `Posted through the ${PMS} API` : `Posted by ELVA’s agent in ${PMS}`}</span>
            </>
          )}
        </div>
        {p.alreadyPosted ? (
          <section className="po-timing" style={{ background: 'var(--muted)' }}>
            <span className="po-tick" aria-hidden="true" style={{ background: 'var(--card)', color: 'var(--mfg)' }}>↺</span>
            <div>Someone posted this payment in {PMS} before ELVA did. ELVA linked it and didn’t post again.</div>
          </section>
        ) : null}

        {p.reverseNote ? (
          <section className="po-timing" style={{ background: 'var(--muted)' }}>
            <span className="po-tick" aria-hidden="true" style={{ background: 'var(--card)', color: 'var(--mfg)' }}>↺</span>
            <div>{p.reverseNote}</div>
          </section>
        ) : null}

        <section className="po-card">
          <h3 className="po-h3" style={{ marginBottom: 10 }}>What was posted</h3>
          <div className="po-tbl-wrap">
            <table className="po-tbl">
              <thead><tr><th>Procedure</th><th className="ta-right">Fee</th><th className="ta-right">{p.payer.short} allowed</th><th className="ta-right">Paid</th><th className="ta-right">Write-off</th><th className="ta-right">{p.firstName} owes</th></tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={`l-${String(i)}`}>
                    <td><strong>{l.code}</strong>{l.tooth ? ` · ${l.tooth}` : ''} · {l.desc}</td>
                    <td className="ta-right num">{money(l.fee)}</td>
                    <td className="ta-right num">{money(l.allowed)}</td>
                    <td className="ta-right num">{money(l.paid)}</td>
                    <td className="ta-right num">{money(l.writeOff)}</td>
                    <td className="ta-right num">{money(l.owes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {fee ? <p className="po-meta" style={{ marginTop: 8 }}>{p.payer.short} paid {money(p.amount)}, the full amount. {fee}</p> : null}
          {oon ? <p className="po-meta" style={{ marginTop: 8 }}>{noWriteOffLabel(p.dentist, p.payer.short)}</p> : null}
          {p.paidToPatient ? <div className="po-note">{p.payer.short} sent its {money(p.paidToPatient.amount)} to {p.firstName}. It isn’t money you receive.</div> : null}
          <p className="po-meta" style={{ marginTop: 8 }}>{p.firstName}’s balance in {PMS}: {balanceWords(p.balanceBefore)} before, {balanceWords(balanceAfter)} after. {codes}</p>
          <div className="po-reffoot">
            <p className="po-meta" style={{ margin: 0 }}>{referenceLine(p.payment, p.payer.short, p.trace)}</p>
          </div>
        </section>

        <section className="po-card">
          <h3 className="po-h3" style={{ marginBottom: 6 }}>How ELVA got here</h3>
          <p style={{ margin: 0, color: 'var(--fg2)' }}>{p.howFound}</p>
          <div className="po-srcrow">{p.sources.map((s) => <span key={s.id} className="po-src">{s.label}</span>)}</div>
        </section>

        <section className="po-card">
          <h3 className="po-h3">What ELVA did in {PMS}</h3>
          {p.how === 'api' ? (
            <>
              <p className="po-meta" style={{ margin: '4px 0 10px' }}>Written through the {PMS} API.</p>
              <table className="po-tbl"><thead><tr><th>Record</th><th>PMS ID</th></tr></thead><tbody>{(p.apiRecords ?? []).map((r) => <tr key={r.id}><td>{r.record}</td><td className="num">{r.id}</td></tr>)}</tbody></table>
            </>
          ) : (
            <>
              <p className="po-meta" style={{ margin: '4px 0 6px' }}>On screen, step by step, the way your team would. Every step recorded.</p>
              {p.log.map((s, i) => (
                <div key={`lg-${String(i)}`} className="po-logr">
                  <span className="t">{s.time}</span>
                  <span>{s.text}</span>
                </div>
              ))}
              <p className="po-meta" style={{ marginTop: 10 }}>Took {p.durationSec} seconds{LOG_ENTRY_FOR_POSTING[p.id] && getLogEntry(LOG_ENTRY_FOR_POSTING[p.id] ?? '') ? <> · <Link to={`/elva-work/log/${LOG_ENTRY_FOR_POSTING[p.id] ?? ''}`} className="po-src">See it in ELVA’s log</Link></> : null}</p>
            </>
          )}
        </section>

        <section className="po-card">
          <h3 className="po-h3" style={{ marginBottom: 6 }}>The money</h3>
          <div className="po-tickrow">
            {p.statementCovers ? <span className="po-tick" aria-hidden="true"><Check /></span> : <span className="po-tick" aria-hidden="true" style={{ background: 'var(--card)', color: 'var(--mfg)' }}>·</span>}
            <div>{p.depositNote}</div>
          </div>
        </section>

        {!p.undone && !p.isUndo && !p.alreadyPosted && hasMoneyPermission() ? (
          <div className="po-card">
            <button type="button" className="po-btn" onClick={() => { setConfirm(true); }}>Undo this posting</button>
            <p className="po-meta" style={{ marginTop: 8 }}>ELVA reverses it in {PMS}, shows you each step, and puts the payment back in To post.</p>
          </div>
        ) : null}
        {undoing ? <div className="po-live" aria-live="polite"><Spinner /><span>{undoStep || 'Undoing…'}</span></div> : null}

        <p className="po-history">History: {p.history.join(' · ')}</p>
      </div>

      {confirm ? (
        <div className="po-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setConfirm(false); }}>
          <div className="po-dialog" role="dialog" aria-modal="true" aria-label="Undo this posting?">
            <h2 className="po-dialog-title">Undo {p.firstName}’s posting of {money(p.amount)}?</h2>
            <p className="po-dialog-body">ELVA reverses this in {PMS} with a reversal entry.</p>
            <div className="po-dialog-actions">
              <button type="button" className="po-btn po-btn-primary" onClick={() => { void runUndo(); }}>Undo</button>
              <button type="button" className="po-btn" onClick={() => { setConfirm(false); }}>Cancel</button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
